/////////////////////////////////////////////////////////
// Configurations related to user.                     //
/////////////////////////////////////////////////////////

// Note: The userFields come from userFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for user fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public', 'protected', or 'private'.
 *                                  Default value: 'public'.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'json', 'json-array', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs on
 *                                  ProfileSettingsPage and AuthenticationPage.
 * - showConfig:                    Configuration for rendering user information. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - displayInProfile (optional):   Can be used to hide field content from profile page.
 *                                    Default value: true.
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for users to fill
 *   - requiredMessage (optional):    Message for mandatory fields.
 *   - displayInSignUp (optional):    Can be used to show field input on sign up page.
 *                                    Default value: true.
 *   - useRadioButtons (optional):    For 'enum' fields, use radio buttons instead of dropdown.
 *                                    Useful for fields with few options that benefit from radio button UX.
 *   - orientation (optional):         Orientation of radio buttons, either 'horizontal' or 'vertical'.
 *                                    Default value: 'vertical'.
 * - userTypeConfig:                Configuration for limiting user field to specific user types.
 *   - limitToUserTypeIds:            Can be used to determine whether to limit the field to certain user types. The
 *                                    Console based asset configurations do not yet support user types, so in hosted configurations
 *                                    the default value for this is 'false'.
 *   - userTypeIds:                   An array of user types for which the extended
 *   (optional)                       data is relevant and should be added.
 */
export const userFields = [
  // Pending category - what the expert requests (only during signup)
  {
    key: 'pendingCategory',
    scope: 'private',
    schemaType: 'enum',
    enumOptions: [
      { option: 'business-story', label: 'Business Story Master' },
      { option: 'content-master', label: 'Content Master' },
      { option: 'education-coaching', label: 'Education & Coaching Master' },
      { option: 'motion-story', label: 'Motion Story Master' },
      { option: 'publishing', label: 'Publishing Master' },
      { option: 'visual-story', label: 'Visual Story Master' },
      { option: 'written-story', label: 'Written Story Master' },
    ],
    showConfig: {
      label: 'Requested Expert Category',
      displayInProfile: false, // Don't show in public profile
    },
    saveConfig: {
      label: 'Select Your Expert Category',
      placeholderMessage: 'Choose your primary area of expertise (requires admin approval)',
      displayInSignUp: true,
      displayInProfileSettings: false, // Don't allow editing after signup
      isRequired: true,
      requiredMessage: 'Please select your expert category',
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  // Category status indicator - internal field
  {
    key: 'categoryStatus',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'pending', label: 'Pending Approval' },
      { option: 'approved', label: 'Approved' },
      { option: 'rejected', label: 'Rejected' },
    ],
    showConfig: {
      label: 'Category Status',
      displayInProfile: false, // We'll show this in a custom way
    },
    saveConfig: {
      label: 'Category Status',
      displayInSignUp: false,
      displayInProfileSettings: false, // Don't show in form
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  // Approved category - what's publicly visible (admin-controlled)
  {
    key: 'category',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'written-story', label: 'Written Story Master' },
      { option: 'motion-story', label: 'Motion Story Master' },
      { option: 'visual-story', label: 'Visual Story Master' },
      { option: 'business-story', label: 'Business Story Master' },
      { option: 'content-master', label: 'Content Master' },
      { option: 'publishing', label: 'Publishing Master' },
      { option: 'education-coaching', label: 'Education & Coaching Master' },
    ],
    showConfig: {
      label: 'Expert Category',
      displayInProfile: false, // Show in public profile
    },
    saveConfig: {
      label: 'Your Expert Category',
      displayInSignUp: false, // Don't show in signup - this is admin-controlled
      displayInProfileSettings: false, // Don't show in form
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  // Approval status - internal field, not user-editable
  {
    key: 'categoryApproved',
    scope: 'public',
    schemaType: 'boolean',
    showConfig: {
      label: 'Category Verified',
      displayInProfile: false, // We'll show a badge instead
    },
    saveConfig: {
      label: 'Category Approval Status',
      displayInSignUp: false,
      displayInProfileSettings: false, // Never show in profile settings
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  // Location field - for all user types
  {
    key: 'location',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Location',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'Location',
      placeholderMessage: 'e.g., New York, NY or London, UK',
      displayInSignUp: false,
      displayInProfileSettings: true,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: false, // Available to all user types
      userTypeIds: [],
    },
  },
  // Headline field - for all user types
  {
    key: 'headline',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Headline',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'Headline',
      placeholderMessage: 'e.g., Award-winning storyteller and content creator',
      displayInSignUp: false,
      displayInProfileSettings: true,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: false, // Available to all user types
      userTypeIds: [],
    },
  },
  // {
  //   key: 'uptalesTitle',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'author', label: 'Author' },
  //     { option: 'motivational-speaker', label: 'Motivational Speaker' },
  //   ],
  //   showConfig: {
  //     label: 'UpTales Title',
  //   },
  //   saveConfig: {
  //     label: 'UpTales Title',
  //     displayInSignUp: false,
  //     isRequired: false,
  //     useRadioButtons: true,
  //     orientation: 'horizontal',
  //   },
  //   userTypeConfig: {
  //     limitToUserTypeIds: true,
  //     userTypeIds: ['expert'],
  //   },
  // },
  {
    key: 'linkedinProfile',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'LinkedIn Profile',
    },
    saveConfig: {
      label: 'LinkedIn Profile',
      placeholderMessage: 'https://linkedin.com/in/yourprofile',
      displayInSignUp: false,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  {
    key: 'twitterProfile',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Twitter Profile',
    },
    saveConfig: {
      label: 'Twitter Profile',
      placeholderMessage: 'https://twitter.com/yourusername',
      displayInSignUp: false,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  {
    key: 'instagramProfile',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Instagram Profile',
    },
    saveConfig: {
      label: 'Instagram Profile',
      placeholderMessage: 'https://instagram.com/yourusername',
      displayInSignUp: false,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  {
    key: 'facebookProfile',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Facebook Profile',
    },
    saveConfig: {
      label: 'Facebook Profile',
      placeholderMessage: 'https://facebook.com/yourprofile',
      displayInSignUp: false,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  {
    key: 'youtubeChannel',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'YouTube Channel',
    },
    saveConfig: {
      label: 'YouTube Channel',
      placeholderMessage: 'https://youtube.com/c/yourchannel',
      displayInSignUp: false,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  {
    key: 'personalWebsite',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Personal Website',
    },
    saveConfig: {
      label: 'Personal Website',
      placeholderMessage: 'https://yourwebsite.com',
      displayInSignUp: false,
      isRequired: false,
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  {
    key: 'pastWorks',
    scope: 'public',
    schemaType: 'json-array',
    showConfig: {
      label: 'Past Works',
      displayInProfile: true,
    },
    saveConfig: {
      label: 'Past Works',
      placeholderMessage: 'Add your past works and projects',
      displayInSignUp: false,
      displayInProfileSettings: true,
      displayInProfile: true,
      isRequired: false,
      // Custom field type to handle complex objects
      fieldType: 'pastWorks',
      orientation: 'vertical',
      // Each work item has these fields
      itemFields: {
        title: {
          label: 'Title',
          type: 'text',
          required: true,
          placeholder: 'Project or work title',
        },
        description: {
          label: 'Description',
          type: 'textarea',
          required: false,
          placeholder: 'Brief description of your work',
        },
        image: {
          label: 'Image',
          type: 'image',
          required: false,
          placeholder: 'Upload an image',
        },
        url: {
          label: 'URL',
          type: 'text',
          required: false,
          placeholder: 'https://example.com/project',
        },
      },
      maxItems: 20, // Maximum number of past works
    },
    userTypeConfig: {
      limitToUserTypeIds: true,
      userTypeIds: ['expert'],
    },
  },
  // {
  //   key: 'cuisine',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'italian', label: 'Italian' },
  //     { option: 'chinese', label: 'Chinese' },
  //     { option: 'thai', label: 'Thai' },
  //   ],
  //   showConfig: {
  //     label: 'Favorite cuisine',
  //   },
  //   saveConfig: {
  //     label: 'Favorite cuisine',
  //     displayInSignUp: true,
  //     isRequired: true,
  //   },
  //   userTypeConfig: {
  //     limitToUserTypeIds: true,
  //     userTypeIds: ['a', 'b', 'c'],
  //   },
  // },
  // {
  //   key: 'canCook',
  //   scope: 'public',
  //   schemaType: 'boolean',
  //   showConfig: {
  //     label: 'Can you cook?',
  //   },
  //   saveConfig: {
  //     label: 'Can you cook?',
  //     displayInSignUp: true,
  //     isRequired: true,
  //     placeholderMessage: 'Select...',
  //   },
  //   userTypeConfig: {
  //     limitToUserTypeIds: true,
  //     userTypeIds: ['a', 'b', 'c'],
  //   },
  // },
  // {
  //   key: 'numberOfCookbooks',
  //   scope: 'public',
  //   schemaType: 'long',
  //   showConfig: {
  //     label: 'How many cookbooks do you have',
  //   },
  //   saveConfig: {
  //     label: 'How many cookbooks do you have',
  //     displayInSignUp: true,
  //     isRequired: true,
  //   },
  //   userTypeConfig: {
  //     limitToUserTypeIds: true,
  //     userTypeIds: ['a', 'b', 'c'],
  //   },
  // },
  // {
  //   key: 'kitchenDescription',
  //   scope: 'public',
  //   schemaType: 'text',
  //   showConfig: {
  //     label: 'Description of your kitchen',
  //   },
  //   saveConfig: {
  //     label: 'Description of your kitchen',
  //     displayInSignUp: true,
  //     isRequired: true,
  //     placeholderMessage: 'Describe your kitchen...',
  //   },
  //   userTypeConfig: {
  //     label: 'Description of your kitchen',
  //     limitToUserTypeIds: true,
  //     userTypeIds: ['a', 'b', 'c'],
  //   },
  // },
  // {
  //   key: 'arrivalInstructions',
  //   scope: 'protected',
  //   schemaType: 'text',
  //   showConfig: {
  //     label: 'How do people arrive at your kitchen?',
  //   },
  //   saveConfig: {
  //     label: 'How do people arrive at your kitchen?',
  //     displayInSignUp: true,
  //     isRequired: true,
  //   },
  //   userTypeConfig: {
  //     limitToUserTypeIds: false,
  //     userTypeIds: ['a', 'b', 'c'],
  //   },
  // },
  // {
  //   key: 'dietaryPreferences',
  //   scope: 'public',
  //   schemaType: 'multi-enum',
  //   enumOptions: [
  //     { option: 'vegan', label: 'Vegan' },
  //     { option: 'vegetarian', label: 'Vegetarian' },
  //     { option: 'gluten-free', label: 'Gluten free' },
  //     { option: 'dairy-free', label: 'Dairy free' },
  //     { option: 'nut-free', label: 'Nut free' },
  //     { option: 'egg-free', label: 'Egg free' },
  //     { option: 'low-carb', label: 'Low carb' },
  //     { option: 'low-fat', label: 'Low fat' },
  //   ],
  //   showConfig: {
  //     label: 'Dietary preferences',
  //   },
  //   saveConfig: {
  //     displayInSignUp: true,
  //     label: 'Dietary preferences',
  //     isRequired: true,
  //   },
  //   userTypeConfig: {
  //     limitToUserTypeIds: true,
  //     userTypeIds: ['a', 'b', 'c'],
  //   },
  // },
];

/////////////////////////////////////
// Example user type configuration //
/////////////////////////////////////
/**
 * User types are not supported in hosted configuration yet.
 *
 * To take user types into use in your
 * custom code, you can do the following things:
 * - Add a new user field with key 'userType', scope 'publicData', and schemaType enum
 *  - Consider whether or not you want to allow your users to change their user type after first creating it
 * - Set your user types as the available options for the userType field
 * - Add your user types in the array below
 * - Update configHelpers.js mergeUserConfig to pass user types to the validUserFields function
 */

export const userTypes = [
  {
    userType: 'buyer',
    label: 'I am a Buyer',
  },
  {
    userType: 'expert',
    label: 'I am an Expert',
  },
  // {
  //   userType: 'seller',
  //   label: 'Seller',
  // },
  // {
  //   userType: 'c',
  //   label: 'Guest',
  // },
  // {
  //   userType: 'd',
  //   label: 'Host',
  // },
];
